// components/CarouselAds.js
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';
import { fetchAds } from '../data/adsData';
import ENV from '../config/env';

const FILE_SERVER_URL = ENV.FILE_SERVER_URL;

const CarouselAds = ({ isVisible, onClose }) => {
    const [currentAd, setCurrentAd] = useState(0);
    const [isAutoPlaying, setIsAutoPlaying] = useState(true);
    const [ads, setAds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lightboxImage, setLightboxImage] = useState(null);

    // Load ads from API on component mount
    useEffect(() => {
        loadAds();
    }, []);

    // Refresh ads every 30 seconds to get updates
    useEffect(() => {
        const interval = setInterval(loadAds, 30000);
        return () => clearInterval(interval);
    }, []);

    const loadAds = async () => {
        try {
            const loadedAds = await fetchAds();
            // Only show active ads
            const activeAds = loadedAds.filter(ad => ad.active);
            setAds(activeAds.sort((a, b) => a.order - b.order));
            setLoading(false);
        } catch (error) {
            console.error('Error loading ads:', error);
            setLoading(false);
        }
    };

    // Auto-rotate ads every 5 seconds
    useEffect(() => {
        if (!isAutoPlaying || !isVisible || ads.length === 0) return;

        const interval = setInterval(() => {
            setCurrentAd((prev) => (prev + 1) % ads.length);
        }, 5000);

        return () => clearInterval(interval);
    }, [isAutoPlaying, isVisible, ads.length]);

    // Navigation
    const nextAd = () => {
        if (ads.length === 0) return;
        setCurrentAd((prev) => (prev + 1) % ads.length);
        setIsAutoPlaying(false);
        setTimeout(() => setIsAutoPlaying(true), 10000);
    };

    const prevAd = () => {
        if (ads.length === 0) return;
        setCurrentAd((prev) => (prev - 1 + ads.length) % ads.length);
        setIsAutoPlaying(false);
        setTimeout(() => setIsAutoPlaying(true), 10000);
    };

    const goToAd = (index) => {
        if (ads.length === 0) return;
        setCurrentAd(index);
        setIsAutoPlaying(false);
        setTimeout(() => setIsAutoPlaying(true), 10000);
    };

    if (!isVisible || loading || ads.length === 0) return null;

    const currentAdData = ads[currentAd];
    if (!currentAdData) return null;

    return (
        <>
            <AnimatePresence>
                <motion.div
                    initial={{ opacity: 0, y: -50 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -50 }}
                    className="fixed bottom-0 left-0 right-0 z-[100] shadow-2xl"
                >
                    <div className="relative">
                        {/* Main Carousel */}
                        <div
                            className="relative overflow-hidden shadow-[0_-4px_10px_-1px_rgba(0,0,0,0.1)]"
                            style={{
                                background: currentAdData.bgColor,
                                minHeight: '180px'
                            }}
                        >
                            {/* Close Button */}
                            <button
                                onClick={onClose}
                                className="absolute top-4 right-4 z-10 text-white hover:text-gray-200 smooth-transition p-2 rounded-full hover:bg-black/20"
                            >
                                <X size={24} />
                            </button>

                            {/* Navigation Arrows */}
                            <button
                                onClick={prevAd}
                                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white p-2 rounded-full glass-effect z-10 hover:bg-white/20"
                            >
                                <ChevronLeft size={24} />
                            </button>

                            <button
                                onClick={nextAd}
                                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white p-2 rounded-full glass-effect z-10 hover:bg-white/20"
                            >
                                <ChevronRight size={24} />
                            </button>

                            {/* Ad Content */}
                            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 md:py-8">
                                <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-8">
                                    {/* Text Content */}
                                    <div className="flex-1 text-center md:text-left w-full max-w-2xl">
                                        <motion.h3
                                            key={`title-${currentAd}`}
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            className="text-xl sm:text-2xl md:text-3xl font-bold mb-2 text-white drop-shadow-md break-words"
                                        >
                                            {currentAdData.title}
                                        </motion.h3>

                                        <motion.div
                                            key={`desc-${currentAd}`}
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: 0.1 }}
                                            className="mb-4 md:mb-6"
                                        >
                                            <p className="text-base sm:text-lg text-white/90 break-words whitespace-pre-wrap leading-relaxed max-h-32 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-white/20">
                                                {currentAdData.description}
                                            </p>
                                        </motion.div>

                                        <motion.a
                                            key={`cta-${currentAd}`}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.2 }}
                                            href={currentAdData.ctaLink}
                                            onClick={onClose}
                                            className="inline-block bg-white text-gray-900 px-5 py-2.5 md:px-6 md:py-3 rounded-full font-semibold hover:bg-gray-100 smooth-transition shadow-lg hover:shadow-xl text-sm md:text-base"
                                        >
                                            {currentAdData.cta}
                                        </motion.a>
                                    </div>

                                    {/* Image/Icon Placeholder */}
                                    <motion.div
                                        key={`img-${currentAd}`}
                                        initial={{ opacity: 0, scale: 0.8 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        className="relative group cursor-pointer flex-shrink-0"
                                        onClick={() => currentAdData.imageUrl && setLightboxImage(currentAdData.imageUrl)}
                                    >
                                        {currentAdData.imageUrl ? (
                                            <div className="relative">
                                                <img
                                                    src={FILE_SERVER_URL + currentAdData.imageUrl}
                                                    alt={currentAdData.title}
                                                    className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 rounded-lg object-cover border-4 border-white/20"
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = `${FILE_SERVER_URL}/images/gallery/default.jpg`;
                                                    }}
                                                />
                                                <div className="absolute inset-0 rounded-lg bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                    <ZoomIn className="text-white" size={24} />
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center">
                                                <span className="text-4xl sm:text-5xl md:text-6xl">
                                                    {currentAdData.title.charAt(0)}
                                                </span>
                                            </div>
                                        )}
                                    </motion.div>
                                </div>
                            </div>
                        </div>

                        {/* Indicators */}
                        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                            {ads.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => goToAd(index)}
                                    className={`w-2 h-2 rounded-full transition-all duration-300 ${index === currentAd
                                        ? 'bg-white w-8'
                                        : 'bg-white/50 hover:bg-white/80'
                                        }`}
                                />
                            ))}
                        </div>

                        {/* Progress Bar */}
                        <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/20">
                            <motion.div
                                key={currentAd}
                                initial={{ width: "100%" }}
                                animate={{ width: "0%" }}
                                transition={{ duration: 5, ease: "linear" }}
                                className="h-full bg-white"
                            />
                        </div>
                    </div>
                </motion.div>

                {/* Backdrop for mobile */}
                {isVisible && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 z-[99] md:hidden"
                        onClick={onClose}
                    />
                )}
            </AnimatePresence>

            {/* Lightbox Modal */}
            <AnimatePresence>
                {lightboxImage && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/90"
                        onClick={() => setLightboxImage(null)}
                    >
                        <button
                            onClick={() => setLightboxImage(null)}
                            className="absolute top-4 right-4 text-white hover:text-gray-300 p-2 rounded-full bg-black/50 z-10"
                        >
                            <X size={32} />
                        </button>

                        <motion.img
                            initial={{ scale: 0.9 }}
                            animate={{ scale: 1 }}
                            src={FILE_SERVER_URL + lightboxImage}
                            alt="Enlarged view"
                            className="max-w-full max-h-[90vh] object-contain rounded-lg"
                            onClick={(e) => e.stopPropagation()}
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = `${FILE_SERVER_URL}/images/gallery/default.jpg`;
                            }}
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default CarouselAds;